import { authenticatedFetch } from "@/lib/auth/authenticatedFetch";
import { getKeycloakInstance } from "@/lib/auth/keycloak";
import { ApiResponseError, extractApiError } from "@/lib/errors/apiError";
import { resolveApiError } from "@/lib/errors/resolveApiError";
import { getApiBaseUrl } from "@/lib/utils";
import { type UseQueryOptions, queryOptions } from "@tanstack/react-query";

import { PRIVILEGED_GROUPS, type User, type UserGroupFilterValue, userGroupFilterValueSchema, userSchema } from "@workspace/schemas/zod";

type UsersResponse = User[];

/**
 * Self-contained query options for fetching users visible to the current user.
 *
 * Reads the signed-in user's Keycloak groups via the singleton instance and
 * decides the fetch strategy automatically:
 *
 * 1. **Privileged** (EPORT_ADMIN / EPORT_MANAGEMENT) — returns all users (no group filter).
 * 2. **Regular user with valid team groups** — returns users filtered by those groups.
 * 3. **No valid team groups** — returns `[]` without hitting the API.
 */
export function teamUsersQueryOptions(options: Omit<UseQueryOptions<UsersResponse>, "queryKey" | "queryFn"> = {}) {
  const keycloak = getKeycloakInstance();
  const userGroups = keycloak.tokenParsed?.group as string[] | undefined;

  const isPrivileged = userGroups?.some((g) => (PRIVILEGED_GROUPS as readonly string[]).includes(g)) ?? false;

  // Privileged users (admin / management) see every user
  if (isPrivileged) {
    return queryOptions({
      queryKey: ["users"],
      queryFn: ({ signal }) => fetchUsers([], signal),
      ...options,
    });
  }

  const validGroups = userGroups ? filterToTeamGroups(userGroups) : [];

  // User has no recognized team groups — return empty without a network call.
  // Distinct key from ["users"] (privileged branch) to avoid cache conflicts.
  if (validGroups.length === 0) {
    return queryOptions({
      queryKey: ["users", "no-team"],
      queryFn: () => Promise.resolve([] as UsersResponse),
      ...options,
    });
  }

  // Regular user — fetch filtered by their team groups
  return queryOptions({
    queryKey: ["users", { groups: validGroups }],
    queryFn: ({ signal }) => fetchUsers(validGroups, signal),
    ...options,
  });
}

/** Filter raw Keycloak groups to only the valid ePort team groups. */
function filterToTeamGroups(groups: string[]): UserGroupFilterValue[] {
  return groups.filter((g): g is UserGroupFilterValue => userGroupFilterValueSchema.safeParse(g).success);
}

async function fetchUsers(groups: UserGroupFilterValue[], signal?: AbortSignal) {
  const baseUrl = getApiBaseUrl();
  const url = new URL(`${baseUrl}/users`);
  for (const group of groups) {
    url.searchParams.append("groups", group);
  }

  const response = await authenticatedFetch(url.toString(), { signal });

  if (!response.ok) {
    const apiError = await extractApiError(response);
    const userMessage = resolveApiError(apiError.error_code, "Benutzer konnten nicht geladen werden");
    throw new ApiResponseError(response.status, apiError.error_code, apiError.details, userMessage);
  }

  // Type assertion needed because z.infer doesn't properly type date fields from callback overrides
  const users = userSchema.array().parse(await response.json()) as unknown as UsersResponse;
  return sortUsersByFirstThenLastName(users);
}

/** First name, then last name as tiebreaker (German locale, accent-sensitive). */
function sortUsersByFirstThenLastName(users: UsersResponse): UsersResponse {
  return [...users].sort((a, b) => {
    const byFirst = a.firstName.localeCompare(b.firstName, "de", { sensitivity: "accent" });
    if (byFirst !== 0) return byFirst;
    return a.lastName.localeCompare(b.lastName, "de", { sensitivity: "accent" });
  });
}
